import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { unwrapResult } from '@reduxjs/toolkit';
import { Country, type ICountry, type IState, State } from 'country-state-city';
import { useSnackbar } from 'notistack';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FieldErrors } from 'react-hook-form/dist/types/errors';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { object, string } from 'yup';
import { LoadingProgress } from '../../../layout';
import { BillingInformation } from '../../../models';
import { formatErrorTrackingMethod } from '../../../pages/checkout/utils';
import { useReadOnlyMode } from '../../../routes';
import { fromAccounts, fromAccountUsers, fromAuth, fromBillingInformation, useAppDispatch } from '../../../store';
import { useGetTrackingCookies, usePlanCartSnapshot } from '../../../hook';
import { sendTrackingData } from '../../../utils/utils';
import { CountrySelector, StateSelector } from './CountriesSelection';
import { BILLING_ADDRESS_TAX_UNRESOLVABLE } from '../payment-billing/BillingDetailsForm';
import { withCartSnapshot } from '../../../utils/analytics';
import { track } from '../../../analytics';

type BillingFormData = Required<Omit<BillingInformation, 'id' | 'createdAt' | 'updatedAt' | 'accountId' | 'archived'>>;
const useStyles = makeStyles(theme => ({
  label: {
    fontWeight: 500,
    fontSize: 14,
    marginBottom: theme.spacing(1),
    color: '#1D3066',
  },
  requiredAsterisk: {
    color: '#0f8461',
    fontWeight: 700,
  },
  description: {
    fontSize: theme.spacing(1.5),
    color: '#62839F',
  },
  warning: {
    fontSize: theme.spacing(1.5),
    fontStyle: 'italic',
    color: 'red',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  informationText: {
    marginBottom: theme.spacing(3),
  },
  getQuoteSubtitle: {
    marginLeft: theme.spacing(1),
    fontSize: theme.spacing(1.75),
  },
  getQuoteTitle: {
    fontSize: theme.spacing(2.5),
    display: 'inline',
  },
  getQuoteTitleContainer: {
    borderBottom: '1px solid #D5D8DD',
    paddingBottom: theme.spacing(2),
  },
  cancelBtn: {
    marginRight: theme.spacing(1),
    fontWeight: 500,
    height: 42,
    float: 'right',
    color: '#000000',
    borderRadius: 'border-radius/border-radius-xs',
    borderWidth: '2px',
  },
  saveBtn: {
    fontWeight: 500,
    height: 42,
    minWidth: 72,
    float: 'right',
    color: '#fff',
    backgroundColor: '#5959EB',
    '&:hover': {
      backgroundColor: '#43509B',
    },
  },
  button: {
    fontWeight: 500,
    height: 42,
    minWidth: 72,
    float: 'right',
    color: '#000000',
    borderRadius: 'border-radius/border-radius-xs',
    borderWidth: '2px',
  },
  billingInformationText: {
    fontWeight: 700,
  },
  billingInformationBox: {
    width: '100%',
    padding: theme.spacing(1, 0, 1, 0),
  },
  // Payment & billing page: the checkout's billing layout (design 6.10, BillingDetailsForm) —
  // a wide left column (Email · Address line · State) next to a narrow right one
  // (Country · City · Postal code).
  redesignGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
    gap: '12px 16px',
  },
  redesignField: {
    minWidth: 0,
  },
  companyToggle: {
    alignSelf: 'flex-start',
    marginTop: 4,
    minHeight: 44,
    padding: 0,
    border: 'none',
    background: 'none',
    font: 'inherit',
    color: '#0b6b4f',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'left',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  companyPanel: {
    marginTop: 4,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
    gap: '12px 16px',
    padding: 16,
    backgroundColor: '#f3f5f4',
    borderRadius: 12,
  },
}));

// Payment & billing page: the checkout's labels / placeholders, so both surfaces read the same.
const REDESIGN_FIELDS: Partial<Record<keyof BillingFormData, {
  labelId: string;
  /** Text inputs only; the country / state selectors bring their own. */
  placeholderId?: string;
  required: boolean;
}>> = {
  ccEmail: {
    labelId: 'page.order.confirmation.v2.billing.email',
    placeholderId: 'page.order.confirmation.v2.billing.email.placeholder',
    required: true,
  },
  country: {
    labelId: 'page.order.confirmation.v2.billing.country',
    required: true,
  },
  address1: {
    labelId: 'page.order.confirmation.v2.billing.address',
    placeholderId: 'page.order.confirmation.v2.billing.address.placeholder',
    required: true,
  },
  city: {
    labelId: 'page.order.confirmation.v2.billing.city',
    placeholderId: 'page.order.confirmation.v2.billing.city.placeholder',
    required: true,
  },
  state: {
    labelId: 'page.order.confirmation.v2.billing.state',
    // Free-text input when the country has no state list (as on checkout).
    placeholderId: 'page.order.confirmation.v2.billing.state.placeholder',
    required: true,
  },
  postalCode: {
    labelId: 'page.order.confirmation.v2.billing.postal',
    placeholderId: 'page.order.confirmation.v2.billing.postal.placeholder',
    required: true,
  },
  fullBusinessName: {
    labelId: 'page.order.confirmation.v2.billing.company.name',
    placeholderId: 'page.order.confirmation.v2.billing.company.name.placeholder',
    required: false,
  },
  vatNumber: {
    labelId: 'page.order.confirmation.v2.billing.company.tax',
    placeholderId: 'page.order.confirmation.v2.billing.company.tax.placeholder',
    required: false,
  },
};

const hasCompanyDetails = (billing?: Partial<BillingInformation>) => !!(
  billing?.fullBusinessName || billing?.vatNumber
);

const inputs: Array<keyof Required<Omit<BillingInformation, 'id' | 'createdAt' | 'updatedAt' | 'accountId' | 'archived'>>> = [
  'fullBusinessName',
  'ccEmail',
  'country',
  'state',
  'city',
  'address1',
  'address2',
  'postalCode',
  'vatNumber',
];

const fieldTrackingInfoMapper: { [key: string]: any } = {
  ccEmail: {
    name: 'cc_email',
    isRequired: false,
  },
  address1: {
    name: 'address_1',
    isRequired: true,
  },
  address2:
    {
      name: 'address_2',
      isRequired: true,
    },
  city: {
    name: 'city',
    isRequired: true,
  },
  state: {
    name: 'state',
    isRequired: true,
  },
  country: {
    name: 'country',
    isRequired: true,
  },
  postalCode: {
    name: 'postal_code',
    isRequired: true,
  },
  vatNumber: {
    name: 'vat_gsc_id',
    isRequired: true,
  },
  fullBusinessName: {
    name: 'business_name',
    isRequired: true,
  },
};

interface BillingInformationComponentProps {
  accountId: number;
  enableSave: boolean;
  /** Payment owns the GET; checkout callers still fetch on mount. */
  pageOwnsBilling?: boolean;
  /**
   * Payment & billing v1 redesign: always-editable form without the
   * Update/Save/Cancel buttons — saving happens via validateAndSave() when the
   * page-level "Continue to payment" button is clicked.
   */
  redesign?: boolean;
  /**
   * Payment & billing page redesign: the section owns the "Billing
   * information" header (title + Edit/Cancel links), so skip the form's
   * built-in title and description.
   */
  hideHeader?: boolean;
}

export const BillingInformationComponent = forwardRef((
  props: BillingInformationComponentProps,
  ref,
) => {
  const classes = useStyles();
  const {
    accountId,
    pageOwnsBilling,
    enableSave,
    redesign,
    hideHeader,
  } = props;
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const formRef = useRef<any>(null);
  const { enqueueSnackbar } = useSnackbar();
  const isEditBillingInfo = useSelector(fromBillingInformation.isEditBillingInfo);
  const account = useSelector(fromAccounts.selectAccountById(Number(accountId)));
  const user = useSelector(fromAuth.selectUser);
  const editBillingInfo = () => {
    dispatch(fromBillingInformation.setEditBillingInfo(true));
  };

  useEffect(() => {
    if (!isEditBillingInfo) {
      dispatch(fromBillingInformation.setBillingInfoValid(true));
    }
  }, [isEditBillingInfo]);
  const currentBillingInformation = useSelector(
    fromBillingInformation.selectOneByAccountId(accountId),
  );
  const billingIsEmpty = useSelector(fromBillingInformation.selectStatusByAccountId(accountId)) === 'empty';
  const isNotSyncedBillingInfo = !!currentBillingInformation && !currentBillingInformation.synced;
  const accountTotalMembers = useSelector(fromAccountUsers.selectCountByAccountId(
    Number(accountId),
  ));
  const cartSnapshot = usePlanCartSnapshot();

  const currentAccountUser = useSelector(fromAccountUsers.selectOneByUserIdAndAccountId(
    Number(user?.id),
    Number(account?.id),
  ));
  const [loading, setLoading] = useState(false);
  // Redesign: the "Company details (optional)" panel, open when the saved address has them.
  const [companyOpen, setCompanyOpen] = useState(hasCompanyDetails(currentBillingInformation));
  const isReadOnly = useReadOnlyMode();

  useEffect(() => {
    if (pageOwnsBilling) return;
    const getBillingInformation = async () => {
      if (accountId) {
        await dispatch(fromBillingInformation.doGetBillingInformation(+accountId));
      }
    };
    getBillingInformation().catch(() => {
    });
  }, [accountId, dispatch, pageOwnsBilling]);
  const schema = object().shape({
    // Payment & billing page: Email and State / Province are required, as on checkout.
    ccEmail: (redesign
      ? string().trim()
        .required(intl.formatMessage({ id: 'page.order.confirmation.v2.billing.email.error' }))
      : string().notRequired())
      .matches(
        /^$|[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
        intl.formatMessage({ id: 'billinginfo.billingcontact.error.email.invalid' }),
      )
      .email(
        intl.formatMessage({ id: 'billinginfo.billingcontact.error.email.invalid' }),
      ),
    // Payment & billing page: company details are optional, as on checkout.
    fullBusinessName: redesign ? string() : string().trim()
      .required(
        intl.formatMessage(
          { id: 'billinginfo.billingcontact.error.required' },
          { field: 'Full Business Name' },
        ),
      ),
    country: string().trim()
      .required(
        intl.formatMessage(
          { id: 'billinginfo.billingcontact.error.required' },
          { field: 'Country' },
        ),
      )
      .matches(
        /^[a-zA-Z,. ]*$/,
        intl.formatMessage({ id: 'billinginfo.billingcontact.error.country.invalid' }),
      ),
    state: redesign
      ? string().trim()
        .required(intl.formatMessage({ id: 'page.order.confirmation.v2.billing.state.error' }))
      : string(),
    // Same City rule as the checkout's BillingDetailsForm.
    city: redesign ? string().trim()
      .required(intl.formatMessage({ id: 'page.order.confirmation.v2.billing.city.error' }))
      .min(2, intl.formatMessage({ id: 'page.order.confirmation.v2.billing.city.short' }))
      .matches(/^\D*$/, intl.formatMessage({ id: 'page.order.confirmation.v2.billing.city.invalid' }))
      : string().trim()
        .required(
          intl.formatMessage(
            { id: 'billinginfo.billingcontact.error.required' },
            { field: 'City' },
          ),
        )
        .matches(
          /^[a-zA-Z ,.;'&/()-]*$/,
          intl.formatMessage({ id: 'billinginfo.billingcontact.error.city.invalid' }),
        ),
    address1: string().trim()
      .required(
        intl.formatMessage(
          { id: 'billinginfo.billingcontact.error.required' },
          { field: 'Address Line 1' },
        ),
      ),
    address2: string(),
    postalCode: string().trim()
      .required(
        intl.formatMessage(
          { id: 'billinginfo.billingcontact.error.required' },
          { field: 'Postal Code' },
        ),
      )
      .matches(
        /^[a-zA-Z0-9 ]*$/,
        intl.formatMessage({ id: 'billinginfo.billingcontact.error.postalCode.invalid' }),
      ),
    vatNumber: string()
      .matches(
        /^[a-zA-Z0-9 .-]*$/,
        intl.formatMessage({ id: 'billinginfo.billingcontact.error.vatId.invalid' }),
      ),
  });

  const [selectedCountry, setSelectedCountry] = useState<ICountry | null>(null);
  const [selectedState, setSelectedState] = useState<IState | null>(null);
  const trackingCookies = useGetTrackingCookies();

  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors, dirtyFields },
    clearErrors,
    setError,
    trigger,
    reset,
  } = useForm<Required<Omit<BillingInformation, 'id' | 'createdAt' | 'updatedAt' | 'accountId' | 'archived'>>>({
    mode: 'onChange',
    defaultValues: currentBillingInformation ?? {
      ccEmail: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      vatNumber: '',
      fullBusinessName: '',
    },
    resolver: yupResolver(schema),
  });

  const mapStateDataToInputs = () => {
    dispatch(fromBillingInformation.setEditBillingInfo(
      !currentBillingInformation || isNotSyncedBillingInfo,
    ));
    if (currentBillingInformation) {
      inputs.forEach(input => {
        setValue(input, (currentBillingInformation[input] ?? '').toString());
      });
    } else if (billingIsEmpty) {
      // A confirmed server-empty clears stale values, never what the user typed.
      inputs.forEach(input => {
        if (!dirtyFields[input]) setValue(input, '');
      });
    }
  };

  // Changing accounts must also clear a draft when both accounts have no stored row.
  useEffect(() => {
    reset({ ccEmail: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      vatNumber: '',
      fullBusinessName: '' });
    setSelectedCountry(null);
    setSelectedState(null);
    setCompanyOpen(false);
  }, [accountId, reset]);

  useEffect(() => {
    mapStateDataToInputs();
    if (hasCompanyDetails(currentBillingInformation)) setCompanyOpen(true);
    if (currentBillingInformation) {
      setSelectedCountry(Country.getAllCountries().find(it => it.isoCode
        === currentBillingInformation.country) || null);
      setSelectedState(State.getStatesOfCountry(currentBillingInformation.country)
        .find(it => it.isoCode === currentBillingInformation.state
          || it.name === currentBillingInformation.state) ?? null);
    } else if (billingIsEmpty) {
      if (!dirtyFields.country) setSelectedCountry(null);
      if (!dirtyFields.state) setSelectedState(null);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [accountId, currentBillingInformation, billingIsEmpty, setValue]);

  const sendInvalidSaveButtonTracking = (errors: string | undefined | string[]) => {
    const additionalInfos = withCartSnapshot({
      field: 'billing_information',
      object: 'button',
      action: 'checkout_page_information_saved',
      is_required: true,
      is_valid: false,
      error: errors,
      ...trackingCookies,
    }, cartSnapshot);
    sendTrackingData(
      'checkout_page_all_action',
      accountId,
      undefined,
      additionalInfos,
    );
  };

  const sendValidSaveButtonTracking = () => {
    const additionalInfos = withCartSnapshot({
      field: 'billing_information',
      object: 'button',
      action: 'checkout_page_information_saved',
      is_required: true,
      is_valid: true,
      ...trackingCookies,
    }, cartSnapshot);
    sendTrackingData(
      'checkout_page_all_action',
      accountId,
      undefined,
      additionalInfos,
    );
  };

  const onSubmitFormError = (fieldErrors: FieldErrors) => {
    if (fieldErrors) {
      const convertedErrors: string[] = Object.keys(errors)
        .map(field => {
          const errorType = (fieldErrors[(field as keyof BillingFormData)]?.type) as string;
          const fieldInfo = fieldTrackingInfoMapper[field];
          return formatErrorTrackingMethod(fieldInfo?.name, errorType);
        });
      sendInvalidSaveButtonTracking(convertedErrors);
    }
  };

  const onBlurredField = (event: React.FocusEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as keyof BillingFormData;
    // A server error (the tax service rejected the address, #1282) stays until the value changes:
    // re-validating on blur would clear it while the address is still wrong.
    const validated = control.getFieldState(fieldName).error?.type === 'server'
      ? Promise.resolve(false)
      : trigger(fieldName);
    validated.then(() => {
      const fieldState = control.getFieldState(fieldName);
      const trackingFieldInfo = fieldTrackingInfoMapper[fieldName];
      let additionalInfos;
      dispatch(fromBillingInformation.setBillingInfoValid(!fieldState.error));
      if (fieldState.error) {
        // Invalid tracking infor
        const isContentDeleted = !event.target.value;
        // Field fullBusinessName - consider delete event
        if (fieldName === 'fullBusinessName' && isContentDeleted) {
          additionalInfos = {
            field: 'billing_information',
            object: trackingFieldInfo.name,
            action: 'delete',
            is_required: trackingFieldInfo.isRequired,
            is_valid: false,
            error: formatErrorTrackingMethod(trackingFieldInfo.name, fieldState.error.type),
          };
        } else {
          additionalInfos = {
            field: 'billing_information',
            object: trackingFieldInfo.name,
            action: 'fill',
            is_required: trackingFieldInfo.isRequired,
            is_valid: false,
            error: formatErrorTrackingMethod(trackingFieldInfo.name, fieldState.error.type),
          };
        }
      } else {
        // Valid tracking infor
        additionalInfos = {
          field: 'billing_information',
          object: trackingFieldInfo.name,
          action: 'fill',
          is_required: trackingFieldInfo.isRequired,
          is_valid: true,
          ...trackingCookies,
        };
      }

      sendTrackingData(
        'checkout_page_all_action',
        accountId,
        undefined,
        withCartSnapshot(additionalInfos, cartSnapshot),
      );
    });
  };

  const renderView = ({ mode, inputKey, field, intl, errors, onBlurredField }: any) => {
    const isView = mode === 'view';
    // Redesign: a country without a state list gets a free-text State / Province (as checkout).
    const stateAsText = redesign && !!selectedCountry
      && State.getStatesOfCountry(selectedCountry.isoCode).length === 0;

    if (inputKey !== 'country' && (inputKey !== 'state' || stateAsText)) {
      return (
        <TextField
          id={inputKey}
          {...field}
          // The form's ref goes on the <input>, not TextField's wrapper, so an error can focus it.
          ref={undefined}
          inputRef={field.ref}
          variant="outlined"
          fullWidth
          placeholder={intl.formatMessage({
            id: (redesign && REDESIGN_FIELDS[inputKey as keyof BillingFormData]?.placeholderId)
              || `billinginfo.billingcontact.placeholder.${inputKey}`,
          })}
          size="small"
          error={!!errors[inputKey]}
          helperText={errors[inputKey]?.message}
          onBlur={onBlurredField}
          fontSize={16}
          disabled={isView}
        />
      );
    }
    if (inputKey === 'state') {
      return (
        <StateSelector
          selectedCountry={selectedCountry}
          selectedState={selectedState}
          setSelectedState={(state: IState) => {
            setSelectedState(state);
          }}
          field={field}
          errors={errors}
          inputKey={inputKey}
          disabled={isView}
        />
      );
    }
    return (
      <CountrySelector
        selectedCountry={selectedCountry}
        setSelectedCountry={(country: ICountry) => {
          setSelectedCountry(country);
          setSelectedState(null);
          // The old state belongs to the old country (checkout clears it the same way).
          if (redesign) setValue('state', '', { shouldValidate: false });
        }}
        field={field}
        errors={errors}
        inputKey={inputKey}
        disabled={isView}
      />
    );
  };

  const formBillingInformation = () => (
    <form
      id="billing-information-form"
      ref={formRef}
      onSubmit={handleSubmit(submitBillingContact, onSubmitFormError)}
    >
      {!hideHeader && (
      <Grid container item>
        <Grid item xs={8}>
          <Typography variant="h3" className={classes.billingInformationText}>
            {intl.formatMessage({ id: 'billing.information' })}
          </Typography>
        </Grid>

        <Grid item xs={4}>
          {enableSave && !redesign && (
            <Button
              variant="contained"
              color="primary"
              className={classes.saveBtn}
              type="submit"
              disabled={loading || isReadOnly}
            >
              {intl.formatMessage({ id: 'billinginfo.button.save' })}
            </Button>
          )}
          {!!currentBillingInformation && !isNotSyncedBillingInfo && !redesign && (
            <Button
              variant="outlined"
              color="inherit"
              className={classes.cancelBtn}
              onClick={handleCancel}
              disabled={loading || isReadOnly}
            >
              {intl.formatMessage({ id: 'billinginfo.button.cancel' })}
            </Button>
          )}
        </Grid>
      </Grid>
      )}
      {!hideHeader && (
      <Typography variant="subtitle2" className={classes.description}>
        {intl.formatMessage({ id: 'billinginfo.billingcontact.description' })}
      </Typography>
      )}
      {enableSave && !currentBillingInformation && !redesign && (
        <Typography variant="subtitle2" className={classes.warning}>
          {intl.formatMessage({ id: 'billinginfo.billingcontact.warning' }, { isGetQuoteUrl: false })}
        </Typography>
      )}
      {redesign ? renderRedesignLayout() : renderBillingInfoLayout('edit')}
      {!hideHeader && (
        <>
          <br />
          <Grid container>
            <Grid item xs={3} md={2} lg={1} />
          </Grid>
        </>
      )}
      {loading && <LoadingProgress fullscreen />}
    </form>
  );

  /**
   * Payment & billing page: the checkout's billing layout (design 6.10) — Email · Country /
   * Address line · City / State · Postal code, then the "Add company details (Optional)"
   * disclosure (Company name · Tax ID). Address line 2 is no longer shown; its saved value
   * stays in the form and is saved back untouched. Folding the company details keeps their
   * values, as on checkout.
   */
  const renderRedesignLayout = () => {
    const renderItem = (inputKey: keyof BillingInformation) => renderBillingItem(inputKey, 'edit');
    const field = (inputKey: keyof BillingInformation, layoutId: string = inputKey) => (
      <Box id={`billing-information-form-layout-${layoutId}`} className={classes.redesignField}>
        {renderItem(inputKey)}
      </Box>
    );
    return (
      <Box id="billing-information-form-layout" display="flex" flexDirection="column">
        <Box className={classes.redesignGrid}>
          {field('ccEmail')}
          {field('country')}
          {field('address1')}
          {field('city')}
          {field('state')}
          {field('postalCode')}
        </Box>
        <button
          type="button"
          id="billing-information-company-toggle"
          className={classes.companyToggle}
          aria-expanded={companyOpen}
          onClick={() => setCompanyOpen(open => !open)}
        >
          <FormattedMessage
            id={companyOpen
              ? 'page.order.confirmation.v2.billing.company.toggle.close'
              : 'page.order.confirmation.v2.billing.company.toggle.open'}
          />
        </button>
        {companyOpen && (
          <Box id="billing-information-company-details" className={classes.companyPanel}>
            {field('fullBusinessName')}
            {field('vatNumber', 'vat')}
          </Box>
        )}
      </Box>
    );
  };

  const renderBillingInfoLayout = (mode: 'view' | 'edit') => {
    const renderItem = (inputKey: keyof BillingInformation) => renderBillingItem(inputKey, mode);

    return (
      <Box
        id="billing-information-form-layout"
        display="flex"
        flexDirection="column"
        paddingY={2}
      >
        <Box
          id="billing-information-form-layout-fullBusinessName-ccEmail"
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          className={classes.billingInformationBox}
        >
          <Box
            id="billing-information-form-layout-fullBusinessName"
            display="flex"
            flexDirection="column"
            width="50%"
            marginRight={2}
          >
            {/* Full Business Name */}
            {renderItem('fullBusinessName')}
          </Box>
          <Box
            id="billing-information-form-layout-ccEmail"
            display="flex"
            flexDirection="column"
            width="50%"
            marginLeft={2}
          >
            {/* CC Email */}
            {renderItem('ccEmail')}
          </Box>
        </Box>
        <Box
          id="billing-information-form-layout-country-state"
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          className={classes.billingInformationBox}
        >
          <Box
            id="billing-information-form-layout-country"
            display="flex"
            flexDirection="column"
            width="50%"
            marginRight={2}
          >
            {/* Country */}
            {renderItem('country')}
          </Box>
          <Box
            id="billing-information-form-layout-state"
            display="flex"
            flexDirection="column"
            width="50%"
            marginLeft={2}
          >
            {/* State */}
            {renderItem('state')}
          </Box>
        </Box>
        <Box
          id="billing-information-form-layout-city-postalCode"
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          className={classes.billingInformationBox}
        >
          <Box
            id="billing-information-form-layout-city"
            display="flex"
            flexDirection="column"
            width="50%"
            marginRight={2}
          >
            {/* City */}
            {renderItem('city')}
          </Box>
          <Box
            id="billing-information-form-layout-postalCode"
            display="flex"
            flexDirection="column"
            width="50%"
            marginLeft={2}
          >
            {/* Postal Code */}
            {renderItem('postalCode')}
          </Box>
        </Box>
        <Box
          id="billing-information-form-layout-address1"
          display="flex"
          flexDirection="column"
          className={classes.billingInformationBox}
        >
          {/* Address 1 */}
          {renderItem('address1')}
        </Box>
        <Box
          id="billing-information-form-layout-address2"
          display="flex"
          flexDirection="column"
          className={classes.billingInformationBox}
        >
          {/* Address 2 */}
          {renderItem('address2')}
        </Box>
        <Box
          id="billing-information-form-layout-vat"
          display="flex"
          flexDirection="column"
          className={classes.billingInformationBox}
        >
          {/* VAT Number */}
          {renderItem('vatNumber')}
        </Box>
      </Box>
    );
  };

  const saveBillingContact = async (formInputs: BillingFormData): Promise<boolean> => {
    // The tax service cannot resolve the address (#1282): the form stays open with what the
    // customer typed and points at the postal code.
    let taxAddressRejected = false;
    setLoading(true);
    const saved = await dispatch(
      fromBillingInformation.doCreateBillingInformation({
        accountId: Number(accountId),
        ...formInputs,
      }),
    )
      .then(unwrapResult)
      .then(() => {
        enqueueSnackbar(
          <FormattedMessage id="billinginfo.billingcontact.update.success" />,
          { variant: 'success' },
        );
        track(
          'Billing Information Saved',
          {
            id: user?.id,
            user_id: user?.email,
            firstName: user?.firstName,
            lastName: user?.lastName,
            email: user?.email,
            system_role: user?.roles,
            org_role: currentAccountUser?.role,
            org_id: account?.id,
            org_name: account?.name,
            total_members: accountTotalMembers,
          },
        );
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          user_id: user?.id,
          org_role: currentAccountUser?.role,
          system_role: user?.roles,
          org_id: account?.id,
          org_name: account?.name,
          total_members: accountTotalMembers,
          event: 'gtm_billing_information_saved',
        });
        sendValidSaveButtonTracking();
        return true;
      })
      .catch((ex: any) => {
        taxAddressRejected = ex?.message === BILLING_ADDRESS_TAX_UNRESOLVABLE;
        if (!taxAddressRejected) mapStateDataToInputs();
        sendInvalidSaveButtonTracking(ex.message);
        return false;
      })
      .finally(() => {
        if (!taxAddressRejected) dispatch(fromBillingInformation.setEditBillingInfo(false));
        setLoading(false);
      });
    // Once the form is editable again, so the field can take focus.
    if (taxAddressRejected) {
      setError(
        'postalCode',
        { type: 'server', message: intl.formatMessage({ id: BILLING_ADDRESS_TAX_UNRESOLVABLE }) },
        { shouldFocus: true },
      );
    }
    return saved;
  };

  const submitBillingContact = (
    inputs: Required<Omit<BillingInformation, 'id' | 'createdAt' | 'updatedAt' | 'accountId' | 'archived'>>,
  ) => {
    saveBillingContact(inputs).catch(() => {
    });
  };

  const handleCancel = () => {
    clearErrors();
    reset(currentBillingInformation, { keepDefaultValues: true });
    dispatch(fromBillingInformation.setEditBillingInfo(false));
  };

  const hasBillingChanges = (formInputs: BillingFormData) => !currentBillingInformation
    || isNotSyncedBillingInfo
    || inputs.some(key => (
      (currentBillingInformation[key] ?? '').toString() !== (formInputs[key] ?? '')
    ));

  useImperativeHandle(ref, () => ({
    saveBillingInformation: () => {
      formRef.current?.requestSubmit();
    },
    /**
     * Validates the form; saves only when something changed (or the stored
     * address is out of sync). Resolves true when checkout can proceed.
     */
    validateAndSave: () => new Promise<boolean>(resolve => {
      handleSubmit(
        formInputs => {
          if (!hasBillingChanges(formInputs)) {
            resolve(true);
            return;
          }
          saveBillingContact(formInputs)
            .then(resolve)
            .catch(() => resolve(false));
        },
        fieldErrors => {
          onSubmitFormError(fieldErrors);
          resolve(false);
        },
      )();
    }),
  }));

  const renderBillingItem = (inputKey: any, mode: string) => {
    const redesignField = redesign ? REDESIGN_FIELDS[inputKey as keyof BillingFormData] : undefined;
    const label = intl.formatMessage({
      id: redesignField?.labelId ?? `billinginfo.billingcontact.${inputKey}`,
    });
    // Redesign mock renders the required marker as a separate " *" instead of the asterisk
    // baked into the legacy translation string.
    const isRequired = redesignField ? redesignField.required : label.endsWith('*');
    const plainLabel = redesignField ? label : label.replace(/\*\s*$/, '').trim();
    return (
      <>
        <Typography
          variant="body1"
          className={classes.label}
        >
          {redesign && isRequired
            ? (
              <>
                {plainLabel}
                <span className={classes.requiredAsterisk}> *</span>
              </>
            )
            : label}
        </Typography>
        <Controller
          render={({ field }) => renderView({
            mode, inputKey, field, intl, errors, onBlurredField,
          })}
          name={inputKey}
          control={control}
        />
      </>
    );
  };

  const isEditMode = redesign || isEditBillingInfo;

  return (
    <Box id="billing-information" paddingTop={3}>
      {!isEditMode && (
        <Grid container>
          <Grid container item>
            <Grid item xs={8}>
              <Typography variant="h6" className={classes.billingInformationText}>
                <FormattedMessage id="billing.information" />
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Button
                id="biling-info-update-btn"
                variant="outlined"
                className={classes.button}
                onClick={editBillingInfo}
                disabled={loading || isReadOnly}
              >
                <FormattedMessage id="billinginfo.button.update" />
              </Button>
            </Grid>
          </Grid>
          <Grid container item>
            <Grid item xs={12}>
              <Typography variant="subtitle2" className={classes.description}>
                {intl.formatMessage({ id: 'billinginfo.billingcontact.description' })}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {!currentBillingInformation && (
                <Typography variant="subtitle2" className={classes.warning}>
                  {intl.formatMessage({ id: 'billinginfo.billingcontact.warning' }, { isGetQuoteUrl: false })}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      )}
      {isEditMode
        ? formBillingInformation() : renderBillingInfoLayout('view')}
    </Box>
  );
});
